import React, { useCallback, useRef, memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom/cjs/react-router-dom';
import { Button, ControlLabel, FormGroup, SelectPicker } from 'rsuite';
import { useFormik } from 'formik';
import styled from 'styled-components';
import { StrRegistrationAPISender } from 'utils/str-registration-api-sender';

import { NoticeBox, Type } from './modules/notice-box';
import { PaymentMethodModule } from 'configurable-form/components/payment/modules/payment-methods/payment-method-module';
import { InputComponent } from 'configurable-form/components/inputs/shared-components';
import * as appSelectors from 'app/state/app/app-selectors';
import { useIsACHActive, useIsCCActive } from 'configurable-form/components/payment/modules/payment-methods/hooks';
import { Helmet } from 'react-helmet';
import { TEST_ZIP_CODE, ERR_EQUIRED_FIELD, TEST_EMAIL, ERR_INVALID_ZIP_CODE, ERR_INVALID_EMAIL, US_STATES, ERR_TOKEN } from 'utils/form-constants';
import dayjs from 'dayjs';

const FORM_WIDTH = 450;

const CYBERSOURCE_SCRIPT = 'https://flex.cybersource.com/cybersource/assets/microform/0.11/flex-microform.min.js';
const ERROR_MICROFORM = 'There was an error loading the data for the payment. Close the tab and try again.';
const ERROR_TIMEOUT_MICROFORM = 'The payment link has expired. Please close this tab, hit the back button on the previous screen and try again.';

// As per https://developer.cybersource.com/docs/cybs/en-us/digital-accept-flex/developer/all/rest/digital-accept-flex/microform-integ-v2/api-reference-v2/class-microform-error-v2.html
const TOKEN_CREATION_TIMEOUT_REASONS = ['CREATE_TOKEN_UNABLE_TO_START', 'CREATE_TOKEN_VALIDATION_SERVERSIDE', 'CREATE_TOKEN_TIMEOUT'];

const CyberSourceContainer = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  margin-top: 3.5rem;
  padding-top: 4.5rem;
  position: relative;
`;

const CyberSourceFormWrapper = styled.div`
  padding: 1.5rem 1rem;
  width: ${FORM_WIDTH}px;
  box-sizing: border-box;
`;

const HeaderText = styled.p`
  color: #5c5d5d;
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 1.5rem;
`;

const CustomFormGroup = styled(FormGroup)`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;
  width: 100%;
`;

const CustomLabel = styled(ControlLabel)`
  padding-right: 20px;
  width: 150px;
`;

const CustomInput = styled(InputComponent)`
  flex-grow: 1;
  width: 100%;
  max-width: 100%;
`;

const CustomInputInner = styled(InputComponent)`
  flex-grow: 1;
  margin: 0;
  margin-bottom: 0.5rem;
  width: 100%;
  max-width: 100%;
`;

const RegionWrapper = styled.div`
  display: grid;
  grid-template-columns: 4fr 3fr 2fr;
  gap: 0.5rem;
`;

const CustomSelect = styled(SelectPicker)`
  flex-grow: 1;
  width: 100%;
`;

const CustomButton = styled(Button)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: #991111;
  font-size: 0.75rem;
`;

const TwoFieldWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 50%;
  margin-top: 1rem;
`;

const FieldContainer = styled.div`
  border-radius: 6px;
  border: 1px solid #e5e5ea;
  box-sizing: border-box;
  height: 36px;
  padding: 7px 11px;
`;

var microformStyles = {
  input: {
    'font-size': '14px',
    'font-family': 'helvetica, tahoma, calibri, sans-serif',
    color: '#555',
    height: '10px',
  },
  ':focus': { color: 'blue' },
  ':disabled': { cursor: 'not-allowed' },
  valid: { color: '#3c763d' },
  invalid: { color: '#a94442' },
};

// From: https://v5.reactrouter.com/web/example/query-parameters
// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const months = new Array(12).fill(1).map((_, index) => ({ label: `${index + 1}`, value: index >= 9 ? `${index + 1}` : `0${index + 1}` }));

const currentYear = dayjs().year();
const years = new Array(12).fill(1).map((_, index) => ({
  label: `${index + currentYear}`,
  value: `${index + currentYear}`,
}));

const us_states = Object.entries(US_STATES);

const regions = us_states.map((us_state) => ({
  label: us_state[1],
  value: us_state[0],
}));

const useCybersource = (place) => {
  const api = useMemo(() => {
    const apiSender = new StrRegistrationAPISender();
    apiSender.setCurrentPlace(place);
    return apiSender;
  }, [place]);

  const postToken = useCallback(
    async (data) => {
      return await api.post('/cyberSourceInitiatePayment', data, 'json');
    },
    [api]
  );

  return {
    postToken,
  };
};

export function PayCyberSource() {
  const query = useQuery();
  const history = useHistory();
  const paymentId = query.get('paymentId');
  const amount = query.get('amount');
  const place = query.get('place');
  const flow = query.get('flow');

  const { postToken } = useCybersource(place);

  const microform = useRef();

  const onSubmit = useCallback(
    (values) => {
      setPosted(true);

      const options = {
        expirationMonth: values.month,
        expirationYear: values.year,
      };

      microform.current.createToken(options, async (err, transientToken) => {
        if (err) {
          console.error('Error generating the token', err);

          if (TOKEN_CREATION_TIMEOUT_REASONS.includes(err.reason)) {
            setErrorMicroform(ERROR_TIMEOUT_MICROFORM);
          } else {
            setError(ERR_TOKEN);
          }
        } else {
          try {
            const address = values.address + ', ' + values.address2;
            const result = await postToken({ ...values, transientToken, paymentId, address, country: 'US' });
            if (result.data.status === 'AUTHORIZED') {
              history.push(`/${flow}/success?paymentId=${paymentId}`);
            } else {
              history.push(`/${flow}/payment-error`);
              console.error('Error executing the payment', result);
            }
          } catch (e) {
            console.error(e);
            setError(e);
          }
        }
        setPosted(false);
      });
    },
    [postToken, flow, history, paymentId]
  );

  const formik = useFormik({
    initialValues: {
      first_name: undefined,
      last_name: undefined,
      address: undefined,
      address2: undefined,
      city: undefined,
      region: undefined,
      postal_code: undefined,
      email: undefined,
      year: years[0].value,
      month: months[0].value,
    },
    validate: (values) => {
      const errors = {};
      if (!values.year) {
        errors.year = ERR_EQUIRED_FIELD;
      }
      if (!values.month) {
        errors.month = ERR_EQUIRED_FIELD;
      }
      if (!values.last_name) {
        errors.last_name = ERR_EQUIRED_FIELD;
      }
      if (!values.first_name) {
        errors.first_name = ERR_EQUIRED_FIELD;
      }
      if (!values.address) {
        errors.address = ERR_EQUIRED_FIELD;
      }
      if (!values.city) {
        errors.city = ERR_EQUIRED_FIELD;
      }
      if (!values.region) {
        errors.region = ERR_EQUIRED_FIELD;
      }
      if (!values.postal_code) {
        errors.postal_code = ERR_EQUIRED_FIELD;
      } else if (!TEST_ZIP_CODE.test(values.postal_code)) {
        errors.postal_code = ERR_INVALID_ZIP_CODE;
      }
      if (!values.email) {
        errors.email = ERR_EQUIRED_FIELD;
      } else if (!TEST_EMAIL.test(values.email)) {
        errors.email = ERR_INVALID_EMAIL;
      }
      return errors;
    },
    onSubmit,
  });

  const [error, setError] = useState(false);

  const [posted, setPosted] = useState(false);
  const flows = useSelector(appSelectors.flows);
  const stats = useSelector(appSelectors.stats);
  const activeFlow = useSelector(appSelectors.activeFlow);
  const isPushACHActive = stats?.app?.enablePushACH || Boolean(flows[activeFlow]?.isPushACHActive);
  const isCCActive = useIsCCActive();
  const isACHActive = useIsACHActive();

  const captureContext = useMemo(() => {
    return window.opener[place][paymentId];
  }, [place, paymentId]);

  const errorInData = useMemo(() => {
    /* Pending to handle correctly the situation where not enough data is provided, we should not render the contents */
    if (!paymentId || !amount || !place) {
      return 'Not enough information to process the payment.';
    }
    return null;
  }, [paymentId, amount, place]);

  const [errorMicroform, setErrorMicroform] = useState();

  const microformScriptLoaded = useCallback(async () => {
    const Flex = window.Flex;
    if (!captureContext) {
      setErrorMicroform(ERROR_MICROFORM);
      console.error(`Context was not properly loaded: ${ERROR_MICROFORM}`);
      return;
    }
    try {
      const flex = new Flex(captureContext);
      microform.current = flex.microform({ styles: microformStyles });
      const number = microform.current.createField('number', {
        placeholder: 'Card number',
      });
      const securityCode = microform.current.createField('securityCode', {
        placeholder: '•••',
      });

      number.load('#number-container');
      securityCode.load('#securityCode-container');
    } catch (e) {
      setErrorMicroform(ERROR_MICROFORM);
      console.error('An error was produced loading the data for the card', e);
    }
  }, [captureContext]);

  const helmetChange = useCallback(
    (_, addedTags) => {
      if (addedTags && addedTags.scriptTags) {
        const scriptTag = addedTags.scriptTags[0];
        scriptTag.onload = microformScriptLoaded;
      }
    },
    [microformScriptLoaded]
  );

  const setYear = useCallback((value) => formik.setFieldValue('year', value), [formik]);

  const setMonth = useCallback((value) => formik.setFieldValue('month', value), [formik]);

  const setRegion = useCallback((value) => formik.setFieldValue('region', value), [formik]);

  const updatePaymentInfo = useCallback((e) => formik.setFieldValue(e.target.name, e.target.value), [formik]);

  if (errorInData) {
    return (
      <CyberSourceContainer>
        <NoticeBox type={Type.WARNING} text={errorInData} />
      </CyberSourceContainer>
    );
  }

  if (errorMicroform) {
    return (
      <CyberSourceContainer>
        <NoticeBox type={Type.WARNING} text={errorMicroform} />
      </CyberSourceContainer>
    );
  }

  return (
    <CyberSourceContainer>
      <Helmet
        script={[
          {
            src: CYBERSOURCE_SCRIPT,
          },
        ]}
        // Helmet doesn't support `onload` in script objects so we have to hack in our own
        onChangeClientState={helmetChange}
      />
      <CyberSourceFormWrapper>
        <HeaderText>Amount Due: ${amount}</HeaderText>
        <PaymentMethodModule isPushACHActive={isPushACHActive} isCCActive={isCCActive} isACHActive={isACHActive && false} />
        {/*paymentDetailsInfo*/}

        <form onSubmit={formik.handleSubmit}>
          {!errorMicroform && <MemoizedCard />}
          <CustomFormGroup>
            <CustomLabel htmlFor="expMonth">Expiry month</CustomLabel>
            <TwoFieldWrapper>
              <CustomFormGroup>
                <CustomSelect onChange={setMonth} value={formik.values.month} data={months} searchable={false} cleanable={false} />
                <ErrorMessage>{formik.errors.month}</ErrorMessage>
              </CustomFormGroup>
              <CustomFormGroup>
                <CustomSelect data={years} searchable={false} required onChange={setYear} value={formik.values.year} cleanable={false} />
                <ErrorMessage>{formik.errors.year}</ErrorMessage>
              </CustomFormGroup>
            </TwoFieldWrapper>
            <ErrorMessage>{error}</ErrorMessage>
          </CustomFormGroup>
          <CustomLabel>Name</CustomLabel>
          <TwoFieldWrapper>
            <CustomFormGroup>
              <CustomInput name="first_name" placeholder="First Name" onChange={updatePaymentInfo} value={formik.values.first_name} />
              <ErrorMessage>{formik.errors.first_name}</ErrorMessage>
            </CustomFormGroup>
            <CustomFormGroup>
              <CustomInput name="last_name" placeholder="Last Name" onChange={updatePaymentInfo} value={formik.values.last_name} />
              <ErrorMessage>{formik.errors.last_name}</ErrorMessage>
            </CustomFormGroup>
          </TwoFieldWrapper>
          <CustomFormGroup>
            <CustomLabel htmlFor="cardholderName">Email</CustomLabel>
            <CustomInput name="email" required placeholder="Email" onChange={updatePaymentInfo} value={formik.values.email} />
            <ErrorMessage>{formik.errors.email}</ErrorMessage>
          </CustomFormGroup>
          <CustomFormGroup>
            <CustomLabel>Address</CustomLabel>
            <div>
              <CustomInputInner required name="address" maxLength={30} onChange={updatePaymentInfo} placeholder="Address Line 1" value={formik.values.address} />
              <ErrorMessage>{formik.errors.address}</ErrorMessage>
              <CustomInputInner id="address2" name="address2" maxLength={30} onChange={updatePaymentInfo} placeholder="Address Line 2 (optional)" value={formik.values.address2} />
              <ErrorMessage>{formik.errors.year}</ErrorMessage>
              <RegionWrapper>
                <div>
                  <CustomInputInner required name="city" maxLength={30} onChange={updatePaymentInfo} placeholder="City" value={formik.values.city} />
                  <ErrorMessage>{formik.errors.city}</ErrorMessage>
                </div>
                <div>
                  <CustomSelect data={regions} searchable required cleanable name="region" onChange={setRegion} placeholder="State" value={formik.values.region} />
                  <ErrorMessage>{formik.errors.region}</ErrorMessage>
                </div>
                <div>
                  <CustomInputInner required name="postal_code" maxLength={10} onChange={updatePaymentInfo} placeholder="Zip Code" value={formik.values.postal_code} type="number" />
                  <ErrorMessage>{formik.errors.postal_code}</ErrorMessage>
                </div>
              </RegionWrapper>
            </div>
          </CustomFormGroup>
          <ButtonsWrapper>
            <CustomButton appearance="primary" id="type-button" loading={posted} type="submit">
              Pay ${amount}
            </CustomButton>
          </ButtonsWrapper>
        </form>
      </CyberSourceFormWrapper>
    </CyberSourceContainer>
  );
}

export const MemoizedCard = memo(function MemoizedCard() {
  return (
    <>
      <CustomFormGroup>
        <label id="cardNumber-label">Card Number</label>
        <FieldContainer id="number-container" />
      </CustomFormGroup>
      <CustomFormGroup>
        <label htmlFor="securityCode-container">Security Code</label>
        <FieldContainer id="securityCode-container" />
      </CustomFormGroup>
    </>
  );
});
